/**
 * Wrap hook passed to `@gctl/openapi-fetcher` codegen via `gctl.config.js`.
 *
 * Every generated fetcher invokes its body inside `measureLatency`. The
 * codegen passes the SDK function name (e.g. `getVideo`) as a literal, so
 * the histogram labels are bounded by the OpenAPI spec and don't require
 * URL parsing. The HTTP method is also passed by the codegen but ignored —
 * the function-name prefix (`get`/`post`/`patch`/…) already encodes it.
 *
 * `status_code` is the exact numeric code on `NetworkError` (the fetcher
 * throws one whenever `!res.ok`); on the success path the codegen body
 * has already discarded the `Response`, so we record the bucket label
 * `'2xx'` instead of the precise 200/201/204.
 *
 * Runs in both browser and Node bundles. The Prometheus bridge
 * (`globalThis.__metrics`) is only installed in the Next.js server runtime
 * by `instrumentation.ts`; everywhere else this is a no-op. The bridge is
 * accessed via a structural cast to avoid colliding with the global
 * declaration in `@vimeo/core/libs/metrics/record`.
 */

type MetricsBridge = {
  histogram(name: string, labels: Record<string, string | number>, value: number): void;
};

const HISTOGRAM = 'vimeo_nextjs_gctl_request_duration_seconds';

function statusFromError(err: unknown): string {
  if (err && typeof err === 'object' && 'status' in err) {
    const status = (err as { status: unknown }).status;
    if (typeof status === 'number') return String(status);
  }
  return 'error';
}

export async function measureLatency<T>(
  operation: string,
  _method: string,
  fn: () => Promise<T>,
): Promise<T> {
  const bridge = (globalThis as { __metrics?: MetricsBridge }).__metrics;
  if (!bridge) return fn();

  const start = performance.now();
  try {
    const result = await fn();
    bridge.histogram(
      HISTOGRAM,
      { operation, status_code: '2xx' },
      (performance.now() - start) / 1000,
    );
    return result;
  } catch (err) {
    bridge.histogram(
      HISTOGRAM,
      { operation, status_code: statusFromError(err) },
      (performance.now() - start) / 1000,
    );
    throw err;
  }
}
